import { createTheme, alpha } from '@mui/material/styles';

const terminalColors = {
  background: '#080c10',
  panel: '#10171d',
  panelRaised: '#141d24',
  border: 'rgba(124, 148, 166, 0.20)',
  borderStrong: 'rgba(124, 148, 166, 0.32)',
  textPrimary: '#e6edf3',
  textSecondary: '#8fa3b4',
  accent: '#4fd1a1',
  accentSoft: '#173c31',
  amber: '#e2a84b',
  blue: '#61afcf',
  red: '#ef6d66',
};

const inputRoot = {
  borderRadius: 6,
  backgroundColor: alpha(terminalColors.background, 0.62),
  '& fieldset': {
    borderColor: terminalColors.border,
  },
  '&:hover fieldset': {
    borderColor: terminalColors.borderStrong,
  },
  '&.Mui-focused fieldset': {
    borderColor: terminalColors.accent,
    boxShadow: `0 0 0 1px ${alpha(terminalColors.accent, 0.28)}`,
  },
};

export const terminalTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: { main: terminalColors.accent, light: '#83e5c0', dark: '#27956c' },
    secondary: { main: terminalColors.amber, light: '#ffd182', dark: '#aa7330' },
    background: { default: terminalColors.background, paper: terminalColors.panel },
    divider: terminalColors.border,
    text: { primary: terminalColors.textPrimary, secondary: terminalColors.textSecondary },
    success: { main: '#5fce8e', dark: '#173625', light: '#8fe6b2' },
    error: { main: terminalColors.red },
    warning: { main: terminalColors.amber },
    info: { main: terminalColors.blue, dark: '#17313d' },
    action: {
      hover: alpha(terminalColors.accent, 0.08),
      selected: alpha(terminalColors.accent, 0.12),
      disabledBackground: alpha('#ffffff', 0.06),
    },
  },
  typography: {
    fontFamily: ['"Noto Sans TC"', '"Microsoft JhengHei"', '"Bahnschrift"', '"Segoe UI"', 'sans-serif'].join(','),
    h4: { fontWeight: 800, letterSpacing: 0, lineHeight: 1.18 },
    h5: { fontWeight: 780, letterSpacing: 0, lineHeight: 1.22 },
    h6: { fontWeight: 760, letterSpacing: 0, lineHeight: 1.25 },
    subtitle2: { fontWeight: 760, letterSpacing: 0 },
    button: { letterSpacing: 0, fontWeight: 760 },
    caption: { letterSpacing: 0 },
  },
  shape: { borderRadius: 6 },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: terminalColors.background,
          backgroundImage:
            'linear-gradient(rgba(255,255,255,0.025) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.018) 1px, transparent 1px)',
          backgroundSize: '28px 28px',
        },
        '::selection': {
          backgroundColor: alpha(terminalColors.accent, 0.3),
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          borderBottom: `1px solid ${terminalColors.border}`,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          backgroundImage: 'none',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          backgroundColor: terminalColors.panel,
          backgroundImage: 'none',
          border: `1px solid ${terminalColors.border}`,
          boxShadow: '0 12px 30px rgba(0, 0, 0, 0.18)',
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          '&:last-child': { paddingBottom: 18 },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 5,
          textTransform: 'none',
          minHeight: 36,
          boxShadow: 'none',
        },
        contained: {
          boxShadow: `0 0 0 1px ${alpha(terminalColors.accent, 0.28)}`,
          '&:hover': { boxShadow: `0 0 0 1px ${alpha(terminalColors.accent, 0.42)}` },
        },
        outlined: {
          borderColor: terminalColors.borderStrong,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          fontWeight: 700,
          letterSpacing: 0,
        },
        sizeSmall: {
          height: 24,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: inputRoot,
        input: {
          paddingTop: 10,
          paddingBottom: 10,
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        size: 'small',
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          color: terminalColors.textSecondary,
        },
      },
    },
    MuiSelect: {
      defaultProps: {
        size: 'small',
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          border: `1px solid ${terminalColors.border}`,
          backgroundImage: 'none',
          alignItems: 'center',
        },
        message: {
          minWidth: 0,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottomColor: terminalColors.border,
          paddingTop: 8,
          paddingBottom: 8,
        },
        head: {
          color: terminalColors.textSecondary,
          fontWeight: 760,
          backgroundColor: alpha(terminalColors.panelRaised, 0.9),
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          minHeight: 42,
          paddingLeft: 12,
          paddingRight: 12,
          fontWeight: 760,
          letterSpacing: 0,
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          borderColor: terminalColors.border,
          textTransform: 'none',
          fontWeight: 700,
          '&.Mui-selected': {
            color: terminalColors.accent,
            backgroundColor: alpha(terminalColors.accent, 0.12),
          },
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: terminalColors.border,
        },
      },
    },
  },
});

export default terminalTheme;
